import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import type { Editor } from "@tiptap/core";
import type { AutoSaveStatus } from "@/hooks/useAutoSave";
import { api, type Block, type DocumentWithBlocks } from "@/lib/api";
import type { BlockType } from "@/components/editor/BlockTypeMenu";
import { getDefaultContent } from "@/components/editor/useBlockTypes";
import { generateUUID } from "@/utils/uuid";
import { tiptapDocToBlocks } from "@/lib/tiptap/document-serializer";

/**
 * Status-whisper metadata pushed by EditorPage so the StatusBar can
 * render inside the document scroll container (Pass 3 contract: status
 * is a margin whisper, not chrome). Lives on the store so the consuming
 * component (BlockEditor → StatusBar) doesn't need prop-drilling.
 */
export interface StatusMeta {
  saveStatus: AutoSaveStatus;
  /** Edits not yet on the server — the sync's own count, not the editor
   *  store's `isDirty`, which nothing clears in Flow (see BlockEditor). */
  hasUnsaved: boolean;
  wordCount: number;
  currentSection: { number?: string; title: string } | null;
  lastSavedAt: Date | null;
}

const generateBlockId = () => generateUUID();

/** Re-number sortOrder on all blocks to match their array index. */
function renumberSortOrders(blocks: Block[]): Block[] {
  return blocks.map((b, i) => (b.sortOrder === i ? b : { ...b, sortOrder: i }));
}

interface BlocksSnapshot {
  blocks: Block[];
  focusedBlockId: string | null;
}

const UNDO_STACK_LIMIT = 50;
const CONTENT_UPDATE_DEBOUNCE_MS = 500;

interface EditorStore {
  // State
  documentId: string | null;
  document: DocumentWithBlocks | null;
  blocks: Block[];
  title: string;
  isDirty: boolean;
  editVersion: number;
  /**
   * Server-only blocks that the rebase merge pulled in but the Flow
   * canvas hasn't yet rendered. BlockEditor drains this queue by
   * calling `insertContentAt` on each entry, which avoids the
   * `setContent` full-doc replace that would wipe in-flight typing.
   * (Background: REGRESSION-002 — see lilia-docs/CHANGELOG.md.)
   */
  pendingRebaseInserts: Block[];
  /** Blocks the server deleted that this editor never touched — removed from the canvas. */
  pendingRebaseRemovals: string[];
  /** Server versions of blocks this editor never touched — swapped into the canvas. */
  pendingRebaseReplacements: Block[];
  focusedBlockId: string | null;
  pendingFocusBlockId: string | null;

  // Undo/Redo
  undoStack: BlocksSnapshot[];
  redoStack: BlocksSnapshot[];
  undo: () => void;
  redo: () => void;

  // Document lifecycle
  initDocument: (doc: DocumentWithBlocks) => void;
  reset: () => void;

  // Title
  setTitle: (title: string) => void;

  // Block operations
  updateBlock: (id: string, content: Record<string, unknown>) => void;
  addBlock: (type: BlockType, index: number) => Block;
  deleteBlock: (blockId: string) => void;
  duplicateBlock: (blockId: string) => Block | null;
  createBlockAfter: (blockId: string) => void;
  createBlockAt: (index: number) => void;
  convertBlock: (blockId: string, newType: BlockType, preserveText?: string) => void;
  setBlocks: (blocks: Block[]) => void;

  // Focus
  setFocusedBlockId: (id: string | null) => void;
  consumePendingFocus: (blockId: string) => boolean;

  // Insert helpers (replace window events)
  insertBlockAfterFocused: (type: BlockType, content?: Record<string, unknown>) => void;
  insertFormulaBlock: (latex: string, label?: string) => void;

  // Citation highlight (click-to-scroll)
  highlightedCiteKey: string | null;
  setHighlightedCiteKey: (key: string | null) => void;

  // Single-editor mode
  editorInstance: Editor | null;
  setEditorInstance: (editor: Editor | null) => void;
  getBlocksForSave: () => Block[];

  // Status-whisper metadata (Pass 3 LILIA-114 / 2026-05-09 round 2):
  // EditorPage owns the autosave + section-tracking hooks but the
  // <StatusBar /> renders inside the document scroll container, so we
  // pass the data through the store rather than prop-drilling.
  statusMeta: StatusMeta | null;
  setStatusMeta: (meta: StatusMeta | null) => void;
}

// AbortController map for convert deduplication (module-scoped, not in store state)
const convertControllers = new Map<string, AbortController>();

/** Module-scoped timestamp for content-update debounce. */
let lastSnapshotTime = 0;

/** Take a snapshot of the current blocks + focus for the undo stack. */
function takeSnapshot(state: { blocks: Block[]; focusedBlockId: string | null }): BlocksSnapshot {
  return { blocks: state.blocks, focusedBlockId: state.focusedBlockId };
}

/**
 * Push the current state onto the undo stack (capped) and clear the redo stack.
 * For content updates (typing), only push if enough time has elapsed.
 */
function pushUndo(
  state: { undoStack: BlocksSnapshot[]; redoStack: BlocksSnapshot[]; blocks: Block[]; focusedBlockId: string | null },
  debounce = false,
): { undoStack: BlocksSnapshot[]; redoStack: BlocksSnapshot[] } {
  if (debounce) {
    const now = Date.now();
    if (now - lastSnapshotTime < CONTENT_UPDATE_DEBOUNCE_MS) {
      return { undoStack: state.undoStack, redoStack: state.redoStack };
    }
    lastSnapshotTime = now;
  }
  const snapshot = takeSnapshot(state);
  const newStack = [...state.undoStack, snapshot];
  if (newStack.length > UNDO_STACK_LIMIT) newStack.shift();
  return { undoStack: newStack, redoStack: [] };
}

export const useEditorStore = create<EditorStore>()(subscribeWithSelector((set, get) => ({
  // Initial state
  documentId: null,
  document: null,
  blocks: [],
  title: "",
  isDirty: false,
  editVersion: 0,
  pendingRebaseInserts: [],
  pendingRebaseRemovals: [],
  pendingRebaseReplacements: [],
  focusedBlockId: null,
  pendingFocusBlockId: null,
  undoStack: [],
  redoStack: [],
  statusMeta: null,

  undo: () => {
    set((state) => {
      if (state.undoStack.length === 0) return state;
      const snapshot = state.undoStack[state.undoStack.length - 1];
      const newUndoStack = state.undoStack.slice(0, -1);
      const current = takeSnapshot(state);
      return {
        blocks: snapshot.blocks,
        focusedBlockId: snapshot.focusedBlockId,
        undoStack: newUndoStack,
        redoStack: [...state.redoStack, current],
        isDirty: true,
      };
    });
  },

  redo: () => {
    set((state) => {
      if (state.redoStack.length === 0) return state;
      const snapshot = state.redoStack[state.redoStack.length - 1];
      const newRedoStack = state.redoStack.slice(0, -1);
      const current = takeSnapshot(state);
      return {
        blocks: snapshot.blocks,
        focusedBlockId: snapshot.focusedBlockId,
        undoStack: [...state.undoStack, current],
        redoStack: newRedoStack,
        isDirty: true,
      };
    });
  },

  initDocument: (doc) => {
    // Cleanup any pending convert requests
    for (const controller of convertControllers.values()) {
      controller.abort();
    }
    convertControllers.clear();

    // Ensure at least one paragraph block exists
    const blocks = doc.blocks.length > 0 ? doc.blocks : [{
      id: generateBlockId(),
      type: "paragraph" as BlockType,
      content: { text: "" },
      sortOrder: 0,
    }];

    set({
      documentId: doc.id,
      document: doc,
      blocks,
      title: doc.title,
      isDirty: false,
      editVersion: 0,
      focusedBlockId: null,
      pendingFocusBlockId: null,
      highlightedCiteKey: null,
      undoStack: [],
      redoStack: [],
    });
  },

  reset: () => {
    for (const controller of convertControllers.values()) {
      controller.abort();
    }
    convertControllers.clear();

    set({
      documentId: null,
      document: null,
      blocks: [],
      title: "",
      isDirty: false,
      editVersion: 0,
      focusedBlockId: null,
      pendingFocusBlockId: null,
      highlightedCiteKey: null,
      undoStack: [],
      redoStack: [],
    });
  },

  setTitle: (title) => {
    set((state) => {
      // Keep Title block content.title in lockstep with the top-bar name so
      // preview (which prefers the Title block) and the canvas stay aligned.
      const hasTitleBlock = state.blocks.some((b) => b.type === "title");
      if (!hasTitleBlock) {
        return { title, isDirty: true };
      }
      return {
        title,
        isDirty: true,
        // Bump editVersion so continuous sync pushes the Title block too.
        editVersion: state.editVersion + 1,
        blocks: state.blocks.map((b) =>
          b.type === "title"
            ? { ...b, content: { ...b.content, title } }
            : b,
        ),
      };
    });
  },

  updateBlock: (id, content) => {
    set((state) => {
      const block = state.blocks.find((b) => b.id === id);
      // Title block title is the single source of truth for the document name.
      const nextTitle =
        block?.type === "title" &&
        typeof content.title === "string" &&
        content.title.trim()
          ? content.title.trim()
          : state.title;
      return {
        ...pushUndo(state, true),
        blocks: state.blocks.map((b) => (b.id === id ? { ...b, content } : b)),
        isDirty: true,
        title: nextTitle,
      };
    });
  },

  addBlock: (type, index) => {
    const newBlock: Block = {
      id: generateBlockId(),
      type,
      content: getDefaultContent(type),
      sortOrder: index,
    };
    set((state) => {
      const newBlocks = [...state.blocks];
      newBlocks.splice(index, 0, newBlock);
      return { ...pushUndo(state), blocks: renumberSortOrders(newBlocks), isDirty: true };
    });
    return newBlock;
  },

  deleteBlock: (blockId) => {
    set((state) => {
      const idx = state.blocks.findIndex((b) => b.id === blockId);
      if (idx === -1) return state;

      const newBlocks = [...state.blocks];
      newBlocks.splice(idx, 1);

      // If we just deleted the last block, insert a fresh empty paragraph
      if (newBlocks.length === 0) {
        newBlocks.push({
          id: generateBlockId(),
          type: "paragraph" as BlockType,
          content: { text: "" },
          sortOrder: 0,
        });
      }

      const focusTarget = newBlocks[Math.max(0, Math.min(idx, newBlocks.length - 1))].id;

      // Schedule DOM focus after React re-renders
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-block-id="${focusTarget}"] [contenteditable="true"]`) as HTMLElement;
        if (el) {
          el.focus();
          const sel = window.getSelection();
          if (sel) {
            const range = document.createRange();
            range.selectNodeContents(el);
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
          }
        } else {
          // For non-TipTap blocks (equation, figure, etc.), focus the block wrapper
          const wrapper = document.querySelector(`[data-block-id="${focusTarget}"]`) as HTMLElement;
          wrapper?.click();
        }
      });

      return {
        ...pushUndo(state),
        blocks: renumberSortOrders(newBlocks),
        pendingFocusBlockId: focusTarget,
        isDirty: true,
      };
    });
  },

  duplicateBlock: (blockId) => {
    const idx = get().blocks.findIndex((b) => b.id === blockId);
    if (idx === -1) return null;

    // Deep clone the source block (blocks are JSON-serializable — they persist
    // as JSON), then override identity + position. Undoable, additive, dirties
    // the doc so auto-save picks it up. Mirrors addBlock's undo/reindex model.
    const source = get().blocks[idx];
    const clone: Block = {
      ...(JSON.parse(JSON.stringify(source)) as Block),
      id: generateBlockId(),
      sortOrder: idx + 1,
    };

    set((state) => {
      const newBlocks = [...state.blocks];
      newBlocks.splice(idx + 1, 0, clone);

      // Focus the copy once it renders (matches deleteBlock's focus handling —
      // contenteditable for text blocks, wrapper click for the rest).
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-block-id="${clone.id}"] [contenteditable="true"]`) as HTMLElement | null;
        if (el) el.focus();
        else (document.querySelector(`[data-block-id="${clone.id}"]`) as HTMLElement | null)?.click();
      });

      return {
        ...pushUndo(state),
        blocks: renumberSortOrders(newBlocks),
        focusedBlockId: clone.id,
        pendingFocusBlockId: clone.id,
        isDirty: true,
      };
    });
    return clone;
  },

  createBlockAfter: (blockId) => {
    const newBlock: Block = {
      id: generateBlockId(),
      type: "paragraph",
      content: { text: "" },
      sortOrder: 0,
    };
    set((state) => {
      const idx = state.blocks.findIndex((b) => b.id === blockId);
      if (idx === -1) return state;
      const insertIdx = idx + 1;
      newBlock.sortOrder = insertIdx;
      const newBlocks = [...state.blocks];
      newBlocks.splice(insertIdx, 0, newBlock);
      return {
        ...pushUndo(state),
        blocks: renumberSortOrders(newBlocks),
        pendingFocusBlockId: newBlock.id,
        isDirty: true,
      };
    });
  },

  createBlockAt: (index) => {
    const newBlock: Block = {
      id: generateBlockId(),
      type: "paragraph",
      content: { text: "" },
      sortOrder: index,
    };
    set((state) => {
      const newBlocks = [...state.blocks];
      newBlocks.splice(index, 0, newBlock);
      return {
        ...pushUndo(state),
        blocks: renumberSortOrders(newBlocks),
        pendingFocusBlockId: newBlock.id,
        isDirty: true,
      };
    });
  },

  convertBlock: (blockId, newType, preserveText) => {
    // Abort any in-flight conversion for the same block
    const existing = convertControllers.get(blockId);
    if (existing) existing.abort();
    const controller = new AbortController();
    convertControllers.set(blockId, controller);

    // Optimistic update
    set((state) => ({
      ...pushUndo(state),
      blocks: state.blocks.map((block) => {
        if (block.id !== blockId) return block;
        const newContent = getDefaultContent(newType);
        if (preserveText !== undefined) {
          if ("text" in newContent) newContent.text = preserveText;
          else if ("code" in newContent) newContent.code = preserveText;
          else if ("latex" in newContent) newContent.latex = preserveText;
        }
        return { ...block, type: newType, content: newContent };
      }),
      isDirty: true,
    }));

    // API call
    const { documentId } = get();
    if (!documentId) return;

    api.blocks.convert(documentId, blockId, newType, controller.signal)
      .then(({ block }) => {
        if (controller.signal.aborted) return;
        set((state) => ({
          blocks: state.blocks.map((b) => {
            if (b.id !== blockId) return b;
            const content = { ...block.content };
            if (preserveText !== undefined) {
              if ("text" in content) (content as Record<string, unknown>).text = preserveText;
              else if ("code" in content) (content as Record<string, unknown>).code = preserveText;
              else if ("latex" in content) (content as Record<string, unknown>).latex = preserveText;
            }
            return { ...b, type: block.type, content };
          }),
        }));
      })
      .catch((err) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        console.error("Failed to convert block via API:", err);
      })
      .finally(() => {
        convertControllers.delete(blockId);
      });
  },

  setBlocks: (blocks) => {
    set((state) => ({ ...pushUndo(state), blocks, isDirty: true }));
  },

  // Focus
  setFocusedBlockId: (id) => {
    set({ focusedBlockId: id });
  },

  consumePendingFocus: (blockId) => {
    const { pendingFocusBlockId } = get();
    if (pendingFocusBlockId === blockId) {
      // Defer the state update to avoid setting state during render
      queueMicrotask(() => set({ pendingFocusBlockId: null }));
      return true;
    }
    return false;
  },

  // Insert helpers — use TipTap editor when available
  insertBlockAfterFocused: (type, content) => {
    const { editorInstance } = get();
    if (editorInstance && !editorInstance.isDestroyed) {
      const nodeTypeMap: Record<string, string> = {
        paragraph: "paragraph", heading: "heading", equation: "equationBlock",
        figure: "figureBlock", code: "codeBlockNode", theorem: "theoremBlock",
        table: "tableBlock", abstract: "abstractBlock", bibliography: "bibliographyBlock",
        tableOfContents: "tocBlock", pageBreak: "pageBreakBlock", columnBreak: "columnBreakBlock",
        list: "listBlock", blockquote: "blockquoteBlock", footnote: "footnoteBlockNode",
        embed: "embedBlock",
      };
      const nodeType = nodeTypeMap[type] || "paragraph";

      // Find the current top-level block
      const { state } = editorInstance;
      let insertPos = state.doc.content.size;
      let replaceEmpty = false;
      let replaceFrom = 0;
      let replaceTo = 0;
      const { $from } = state.selection;
      // Walk up to find the top-level node (depth 1)
      if ($from.depth >= 1) {
        const topNodePos = $from.before(1);
        const topNode = state.doc.nodeAt(topNodePos);
        if (topNode) {
          insertPos = topNodePos + topNode.nodeSize;
          // If current block is an empty paragraph, replace it instead of inserting after
          if (topNode.type.name === "paragraph" && topNode.textContent.trim() === "") {
            replaceEmpty = true;
            replaceFrom = topNodePos;
            replaceTo = topNodePos + topNode.nodeSize;
          }
        }
      }

      if (type === "paragraph") {
        const newNode = {
          type: "paragraph",
          attrs: { blockId: replaceEmpty ? undefined : generateBlockId() },
          content: content?.text ? [{ type: "text", text: content.text as string }] : [],
        };
        if (replaceEmpty) {
          editorInstance.chain().focus().insertContentAt({ from: replaceFrom, to: replaceTo }, newNode).run();
        } else {
          editorInstance.chain().focus().insertContentAt(insertPos, newNode).run();
        }
      } else if (type === "heading") {
        const level = (content?.level as number) || 1;
        const newNode = {
          type: "heading",
          attrs: { level, blockId: generateBlockId() },
          content: content?.text ? [{ type: "text", text: content.text as string }] : [],
        };
        if (replaceEmpty) {
          editorInstance.chain().focus().insertContentAt({ from: replaceFrom, to: replaceTo }, newNode).run();
        } else {
          editorInstance.chain().focus().insertContentAt(insertPos, newNode).run();
        }
      } else {
        const attrs = { blockId: generateBlockId(), ...content || getDefaultContent(type) };
        const newNode = { type: nodeType, attrs };
        if (replaceEmpty) {
          editorInstance.chain().focus().insertContentAt({ from: replaceFrom, to: replaceTo }, newNode).run();
        } else {
          editorInstance.chain().focus().insertContentAt(insertPos, newNode).run();
        }
      }
      return;
    }

    // Fallback: direct blocks array manipulation (legacy)
    const { blocks, focusedBlockId } = get();
    let insertIdx = blocks.length;
    if (focusedBlockId) {
      const idx = blocks.findIndex((b) => b.id === focusedBlockId);
      if (idx !== -1) insertIdx = idx + 1;
    }
    const newBlock: Block = {
      id: generateBlockId(),
      type,
      content: content || getDefaultContent(type),
      sortOrder: insertIdx,
    };
    set((state) => {
      const newBlocks = [...state.blocks];
      newBlocks.splice(insertIdx, 0, newBlock);
      return {
        ...pushUndo(state),
        blocks: renumberSortOrders(newBlocks),
        pendingFocusBlockId: newBlock.id,
        isDirty: true,
      };
    });
  },

  insertFormulaBlock: (latex, label) => {
    const { editorInstance } = get();
    if (editorInstance && !editorInstance.isDestroyed) {
      const { state } = editorInstance;
      let insertPos = state.doc.content.size;
      const { $from } = state.selection;
      if ($from.depth >= 1) {
        const topNodePos = $from.before(1);
        const topNode = state.doc.nodeAt(topNodePos);
        if (topNode) {
          insertPos = topNodePos + topNode.nodeSize;
        }
      }
      editorInstance.chain().focus().insertContentAt(insertPos, {
        type: "equationBlock",
        attrs: {
          blockId: generateBlockId(),
          latex,
          equationMode: "display",
          label: label || "",
        },
      }).run();
      return;
    }

    // Fallback: direct blocks array manipulation (legacy)
    const { blocks, focusedBlockId } = get();
    let insertIdx = blocks.length;
    if (focusedBlockId) {
      const idx = blocks.findIndex((b) => b.id === focusedBlockId);
      if (idx !== -1) insertIdx = idx + 1;
    }
    const newBlock: Block = {
      id: generateBlockId(),
      type: "equation",
      content: { latex, equationMode: "display", label: label || "" },
      sortOrder: insertIdx,
    };
    set((state) => {
      const newBlocks = [...state.blocks];
      newBlocks.splice(insertIdx, 0, newBlock);
      return {
        ...pushUndo(state),
        blocks: renumberSortOrders(newBlocks),
        isDirty: true,
      };
    });
  },

  // Citation highlight (click-to-scroll)
  highlightedCiteKey: null,
  setHighlightedCiteKey: (key) => {
    set({ highlightedCiteKey: key });
  },

  // Single-editor mode
  editorInstance: null,
  setEditorInstance: (editor) => {
    set({ editorInstance: editor });
  },
  getBlocksForSave: () => {
    const { editorInstance, blocks } = get();
    if (editorInstance && !editorInstance.isDestroyed) {
      return tiptapDocToBlocks(editorInstance.getJSON());
    }
    // Fallback to existing blocks array
    return blocks;
  },

  setStatusMeta: (meta) => {
    set({ statusMeta: meta });
  },
})));
